/**
 * AI-JAVADOC: UfFormTypeObjectComponent
 * Module: UF (Universal Forms)
 * Type: ObjectBaseComponent
 * Description: Object detail view for UfFormType entity with three navigation tabs:
 *              - Übersicht: Overview detail of the form type
 *              - Felder: Table widget of UfFormTypeField entries filtered by type
 *              - Objektreferenzen: Table widget of UfFormTypeObjectReference entries filtered by formType
 */
import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ConfirmationService} from 'primeng/api';
import {Subscription} from 'rxjs';
import {ActivatedRoute} from '@angular/router';
import {ObjectBaseComponent} from '../../../../../core/shared/object/mvs-object-base/object-base.component';
import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../core/shared/service/message/mvs-message.service';
import {ObserverService} from '../../../../../core/shared/object/service/observer.service';
import {NavigationItem} from '../../../../../core/shared/dto/navigation/navigation-item';
import {WidgetData} from '../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../core/shared/widget/service/widget.factory';
import {FilterCriteria} from '../../../../../core/shared/filter/api/filter.criteria';
import {
    WidgetFunctionCallBackCreate,
    WidgetToolbarCreateInterface
} from '../../../../../core/shared/helper/widget-function-call-back-create';
import {UfFormTypeDto} from '../../../model/dto/entity/uf-form-type.dto';

@Component({
    selector: 'uf-form-type-object-component',
    templateUrl: 'uf-form-type-object.component.html',
    styleUrls: ['uf-form-type-object.component.scss'],
    standalone: false
})
export class UfFormTypeObjectComponent extends ObjectBaseComponent implements OnInit, OnChanges, OnDestroy {

    dto: UfFormTypeDto;

    fieldsWidget: WidgetData;
    objectReferencesWidget: WidgetData;

    navigationItems: NavigationItem[] = [
        {
            label: 'Übersicht',
            action: 'overview',
            tooltip: 'Übersicht des Formulartyps',
            icon: 'fa-regular fa-eye',
            toggleable: false,
            default: true
        },
        {
            label: 'Felder',
            action: 'fields',
            tooltip: 'Felder des Formulartyps',
            icon: 'fa-regular fa-list-check',
            toggleable: false,
            default: false
        },
        {
            label: 'Objektreferenzen',
            action: 'objectReferences',
            tooltip: 'Objektreferenzen des Formulartyps',
            icon: 'fa-regular fa-link',
            toggleable: false,
            default: false
        }
    ];

    private queryParamSubscription: Subscription;

    constructor(
        protected coreService: MvsCoreService,
        protected messageService: MvsMessageService,
        protected confirmationService: ConfirmationService,
        protected override observerService: ObserverService,
        private route: ActivatedRoute
    ) {
        super(coreService, messageService, confirmationService, observerService);
    }

    ngOnInit() {
        super.ngOnInit();
        this.subscribeToNavItemQueryParam();
    }

    /**
     * Called when the object (UfFormType) is loaded or refreshed.
     * Emits navigation items and creates child widgets.
     */
    onObjectChanged() {
        if (!this.activeNavigationItems) {
            this.activeNavigationItems = this.navigationItems[0];
        }
        this.onNavigationItems.emit(this.navigationItems);
        this.createChildWidgets();
    }

    /**
     * Subscribe to route query params to support ?navItem= URL navigation switching.
     */
    private subscribeToNavItemQueryParam(): void {
        this.queryParamSubscription = this.route.queryParams.subscribe(params => {
            const navItem = params['navItem'];
            if (navItem) {
                const found = this.navigationItems.find(item => item.action === navItem);
                if (found) {
                    this.activeNavigationItems = found;
                }
            }
        });
    }

    /**
     * Create child widgets for the Felder and Objektreferenzen tabs.
     */
    private createChildWidgets(): void {
        const formTypeId = this.objectIdentifier.objectId;

        // --- Fields Widget ---
        const fieldsFilter = [
            FilterCriteria.create('e.typeId', FilterCriteria.cOperatorEqual, formTypeId)
        ];
        /**
         * AI:
         * Status: 'in progress'
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: UfFormTypeField table widget filtered by form type for displaying form type fields
         */
        this.fieldsWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'uf.formType.object.fields.table',
            'Felder',
            'uf.UfFormTypeField',
            fieldsFilter
        );

        const fieldsCreateFields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'typeId', fieldValue: formTypeId}
        ];
        this.fieldsWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(fieldsCreateFields);

        // --- Object References Widget ---
        const objectReferencesFilter = [
            FilterCriteria.create('e.formTypeId', FilterCriteria.cOperatorEqual, formTypeId)
        ];
        /**
         * AI:
         * Status: 'in progress'
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: UfFormTypeObjectReference table widget filtered by form type for displaying object references
         */
        this.objectReferencesWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'uf.formType.object.objectReferences.table',
            'Objektreferenzen',
            'uf.UfFormTypeObjectReference',
            objectReferencesFilter
        );

        const objectReferencesCreateFields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'formTypeId', fieldValue: formTypeId}
        ];
        this.objectReferencesWidget.functionCallbacks =
            WidgetFunctionCallBackCreate.widgetToolbarCreate(objectReferencesCreateFields);
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.queryParamSubscription?.unsubscribe();
    }
}
